/**
 * Query 参数格式配置
 * 用于统一处理不同 API 接口的参数格式转换
 */
import dayjs from 'dayjs'

/**
 * ERP API 日期格式配置
 * key: 转换类型，value: 目标格式
 */
export const ERP_DATE_FORMAT = {
  day: 'YYYY/MM/DD',
  month: 'YYYY/MM'
}

/**
 * 格式化日期值
 * @param {string|Date} date - 原始日期值
 * @param {string} key - 转换类型：'day' | 'month'
 * @returns {string} 格式化后的日期字符串
 * 
 * @example
 * formatERPDate('2025-12-17', 'day')   // 返回 '2025/12/17'
 * formatERPDate('2025-12-17', 'month') // 返回 '2025/12'
 */
export const formatERPDate = (date, key = 'day') => {
  if (!date) {
    return date
  }

  const format = ERP_DATE_FORMAT[key]
  if (!format) {
    console.warn(`[queryConfig] 不支持的转换类型: ${key}，保持原值`)
    return date
  }

  try {
    const dateObj = dayjs(date)
    if (!dateObj.isValid()) {
      console.warn(`[queryConfig] 日期格式无效: ${date}，保持原值`)
      return date
    }

    const formattedValue = dateObj.format(format)
    
    if (process.env.NODE_ENV === 'development') {
      // console.log(`[queryConfig] 日期转换: "${date}" -> "${formattedValue}" (类型: ${key}, 格式: ${format})`)
    }
    
    return formattedValue
  } catch (error) {
    console.warn(`[queryConfig] 日期转换失败: ${error.message}，保持原值`)
    return date
  }
}

/**
 * ERP API 参数转换函数（兼容旧接口）
 * @param {Object|string} query - 原始查询参数或日期值
 * @param {Object} options - 转换选项（可选）
 * @returns {Object|string} 转换后的查询参数或日期字符串
 * 
 * @example
 * // 单值转换模式（新用法）
 * transformERPQuery({ data: '2025-12-17', monthType: 'date' })   // 返回 '2025/12/17'
 * transformERPQuery({ data: '2025-12-17', monthType: 'month' })  // 返回 '2025/12'
 * 
 * // 对象转换模式（兼容旧用法）
 * transformERPQuery({ date: '2025-12-17', store_id: 59 })
 */
export const transformERPQuery = (query, options = {}) => {
  if (!query || typeof query !== 'object') {
    return query
  }

  // 单值转换模式：如果传入 { data: xxx, monthType: 'month' | 'date' }，只转换这个值并返回
  if (query.data !== undefined && (query.monthType || options.monthType)) {
    const monthType = query.monthType || options.monthType
    const key = monthType === 'date' ? 'day' : monthType === 'month' ? 'month' : 'day'
    return formatERPDate(query.data, key)
  }

  // 对象转换模式：转换整个 query 对象
  const erpQuery = { ...query }

  // 处理 date 字段
  if (erpQuery.date) {
    erpQuery.date = formatERPDate(erpQuery.date, 'day')
  }

  // 处理 month 字段
  if (erpQuery.month) {
    erpQuery.month = formatERPDate(erpQuery.month, 'month')
  }

  // 调试日志
  if (process.env.NODE_ENV === 'development') {
    // console.log('[queryConfig] 转换前的 query:', query)
    // console.log('[queryConfig] 转换后的 query:', erpQuery)
  }

  return erpQuery
}

/**
 * 默认参数转换函数（不做任何转换）
 * 用于非 ERP 接口，保持原样
 */
export const transformDefaultQuery = (query) => {
  return query
}
